// src/services/bookAccessService.js
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

class BookAccessService {
  // Check if user can access a book
  async canAccessBook(bookId, userId = null) {
    try {
      const bookDoc = await getDoc(doc(db, 'pb-books', bookId));
      
      if (!bookDoc.exists()) {
        return { canAccess: false, reason: 'Book not found' };
      }

      const book = bookDoc.data();

      // Public books can be accessed by anyone
      if (book.accessLevel === 'public') {
        return { canAccess: true, book };
      }

      // Private/Restricted books require authentication
      if (book.accessLevel === 'private' || book.accessLevel === 'restricted') {
        if (!userId) {
          return { canAccess: false, reason: 'Authentication required' };
        }

        // Check if user is the owner
        if (book.ownerId === userId) {
          return { canAccess: true, book };
        }

        // FIX: Check user email against authorized emails
        if (book.authorizedUsers && book.authorizedUsers.length > 0) {
          // Get user's email from their profile
          const userDoc = await getDoc(doc(db, 'pb-users', userId));
          const userData = userDoc.data();
          
          if (userData?.email && book.authorizedUsers.includes(userData.email)) {
            return { canAccess: true, book };
          }
        }

        return { canAccess: false, reason: 'Access denied' };
      }

      // Organization level access
      if (book.accessLevel === 'organization') {
        if (!userId) {
          return { canAccess: false, reason: 'Authentication required' };
        }

        const userDoc = await getDoc(doc(db, 'pb-users', userId));
        const userData = userDoc.data();

        if (userData?.organizationId === book.organizationId) {
          return { canAccess: true, book };
        }

        return { canAccess: false, reason: 'Organization access required' };
      }

      return { canAccess: false, reason: 'Invalid access level' };
    } catch (error) {
      console.error('Error checking book access:', error);
      return { canAccess: false, reason: 'Error checking access' };
    }
  }

  // Get books accessible to user
  async getUserAccessibleBooks(userId) {
    try {
      const queries = [
      query(collection(db, 'pb-books'),
            where('accessLevel', '==', 'public'),
            where('published', '==', true)),
      query(collection(db, 'pb-books'),
            where('ownerId', '==', userId)),
      query(collection(db, 'pb-books'),
            where('authorizedUsers', 'array-contains', userId)),
    ];

    const settled = await Promise.allSettled(queries.map(getDocs));
    const books = [];
    const seen = new Set();

    for (const r of settled) {
      if (r.status !== 'fulfilled') continue; // ignore permission/other failures
      r.value.docs.forEach(d => {
        if (!seen.has(d.id)) {
          seen.add(d.id);
          books.push({ id: d.id, ...d.data() });
        }
      });
    }

    return books;
    } catch (error) {
      console.error('Error getting accessible books:', error);
      return [];
    }
  }

  // Get public books (no auth required)
  async getPublicBooks() {
    try {
      const q = query(
        collection(db, 'pb-books'),
        where('accessLevel', '==', 'public'),
        where('published', '==', true)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting public books:', error);
      return [];
    }
  }
}

export const bookAccessService = new BookAccessService();